// src/pages/reporteria/DashboardReporteria.jsx
import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Row, 
  Col, 
  Statistic, 
  DatePicker, 
  Button, 
  Space, 
  Spin,
  Alert,
  Select,
  Badge,
  Typography,
  Divider,
  Progress,
  Empty
} from 'antd';
import {
  DollarOutlined,
  UserOutlined,
  TeamOutlined,
  RiseOutlined,
  FallOutlined,
  CalendarOutlined,
  TrophyOutlined,
  PercentageOutlined,
  ShopOutlined,
  LineChartOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import ReporteriaService from '../../../../service/Reporteria';
import { useNavigate } from 'react-router-dom';

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;
const { Option } = Select;

const DashboardReporteria = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [fechas, setFechas] = useState([
    dayjs().startOf('month'),
    dayjs().endOf('month')
  ]);
  const [periodo, setPeriodo] = useState('mes_actual');
  
  // Estados para los diferentes reportes
  const [datosReporte, setDatosReporte] = useState(null);

  // Cargar datos del dashboard
  const cargarDatos = async () => {
    setLoading(true);
    try {
      const params = {
        fechaInicio: fechas[0].format('YYYY-MM-DD'),
        fechaFin: fechas[1].format('YYYY-MM-DD')
      };

      // Cargar todos los reportes en paralelo
      const [
        ingresos,
        ocupacion,
        inscripciones,
        pagos
      ] = await Promise.all([
        ReporteriaService.GetResumenIngresos(params),
        ReporteriaService.GetResumenOcupacion(params),
        ReporteriaService.GetInscripcionesPorEstado(params),
        ReporteriaService.GetEstadoPagos(params)
      ]);

      setDatosReporte({
        ingresos: ingresos,
        ocupacion: ocupacion,
        inscripciones: inscripciones,
        pagos: pagos
      });

    } catch (error) {
      console.error('Error cargando datos del dashboard:', error);
      setDatosReporte(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargarDatos();
  }, [fechas]);

  // Manejador de cambio de periodo predefinido
  const handlePeriodoChange = (value) => {
    setPeriodo(value);
    const hoy = dayjs();
    
    switch (value) {
      case 'hoy':
        setFechas([hoy.startOf('day'), hoy.endOf('day')]);
        break;
      case 'semana_actual':
        setFechas([hoy.startOf('week'), hoy.endOf('week')]);
        break;
      case 'mes_actual':
        setFechas([hoy.startOf('month'), hoy.endOf('month')]);
        break;
      case 'trimestre_actual':
        setFechas([hoy.startOf('quarter'), hoy.endOf('quarter')]);
        break;
      case 'año_actual':
        setFechas([hoy.startOf('year'), hoy.endOf('year')]);
        break;
      case 'mes_pasado':
        setFechas([
          hoy.subtract(1, 'month').startOf('month'),
          hoy.subtract(1, 'month').endOf('month')
        ]);
        break;
      case 'ultimos_30_dias':
        setFechas([hoy.subtract(30, 'day'), hoy]);
        break;
      case 'ultimos_90_dias':
        setFechas([hoy.subtract(90, 'day'), hoy]);
        break;
      default:
        break;
    }
  };

  // Calcular cambio porcentual
  const calcularCambio = (actual, anterior) => {
    if (!anterior || anterior === 0) return null;
    const cambio = ((actual - anterior) / anterior * 100).toFixed(1);
    return cambio;
  };

  // Formatear moneda chilena
  const formatearMoneda = (monto) => {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
      minimumFractionDigits: 0
    }).format(monto || 0);
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-6">
        <Title level={2} className="mb-2">
          <LineChartOutlined className="mr-2" />
          Dashboard de Reportería
        </Title>
        <Text type="secondary">
          Resumen general de métricas y estadísticas del sistema
        </Text>
      </div>

      {/* Filtros */}
      <Card className="mb-6 shadow-sm">
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={12} md={8} lg={6}>
            <Space direction="vertical" className="w-full">
              <Text strong>Periodo Predefinido:</Text>
              <Select
                value={periodo}
                onChange={handlePeriodoChange}
                className="w-full"
                size="large"
              >
                <Option value="hoy">Hoy</Option>
                <Option value="semana_actual">Semana Actual</Option>
                <Option value="mes_actual">Mes Actual</Option>
                <Option value="trimestre_actual">Trimestre Actual</Option>
                <Option value="año_actual">Año Actual</Option>
                <Option value="mes_pasado">Mes Pasado</Option>
                <Option value="ultimos_30_dias">Últimos 30 días</Option>
                <Option value="ultimos_90_dias">Últimos 90 días</Option>
              </Select>
            </Space>
          </Col>
          
          <Col xs={24} sm={12} md={10} lg={8}>
            <Space direction="vertical" className="w-full">
              <Text strong>Rango Personalizado:</Text>
              <RangePicker
                value={fechas}
                onChange={(dates) => {
                  if (dates) {
                    setFechas(dates);
                    setPeriodo('personalizado');
                  }
                }}
                format="DD/MM/YYYY"
                className="w-full"
                size="large"
              />
            </Space>
          </Col>
          
          <Col xs={24} sm={24} md={6} lg={4}>
            <Button 
              type="primary" 
              size="large"
              onClick={cargarDatos}
              loading={loading}
              className="w-full mt-6"
              style={{ backgroundColor: '#0047bb', borderColor: '#0047bb' }}
            >
              Actualizar
            </Button>
          </Col>
        </Row>
      </Card>

      {loading ? (
        <div className="flex justify-center items-center py-20">
          <Spin size="large" tip="Cargando datos..." />
        </div>
      ) : !datosReporte ? (
        <Alert
          message="Error al cargar datos"
          description="No se pudieron cargar los datos del dashboard. Por favor intenta nuevamente."
          type="error"
          showIcon
        />
      ) : (
        <>
          {/* KPIs Principales */}
          <Row gutter={[16, 16]} className="mb-6">
            <Col xs={24} sm={12} lg={6}>
              <Card className="shadow-sm hover:shadow-md transition-shadow">
                <Statistic
                  title={
                    <span className="text-gray-600 font-medium">
                      <DollarOutlined className="mr-2" />
                      Ingresos Totales
                    </span>
                  }
                  value={datosReporte.ingresos?.ingresosPorPeriodo?.totales?.totalIngresos || 0}
                  precision={0}
                  prefix="$"
                  valueStyle={{ color: '#0047bb', fontSize: '28px', fontWeight: 'bold' }}
                />
                <Divider className="my-2" />
                <div className="flex justify-between items-center">
                  <Text type="secondary" className="text-xs">
                    {datosReporte.ingresos?.ingresosPorPeriodo?.totales?.cantidadPagos || 0} pagos
                  </Text>
                  {datosReporte.ingresos?.ingresosPorPeriodo?.comparacion && (
                    <Text 
                      type={datosReporte.ingresos.ingresosPorPeriodo.comparacion.tendencia === 'crecimiento' ? 'success' : 'secondary'}
                      className="text-xs"
                    >
                      {datosReporte.ingresos.ingresosPorPeriodo.comparacion.tendencia === 'crecimiento' ? '↑' : '→'} 
                      {' '}{datosReporte.ingresos.ingresosPorPeriodo.comparacion.tendencia}
                    </Text>
                  )}
                </div>
              </Card>
            </Col>

            <Col xs={24} sm={12} lg={6}>
              <Card className="shadow-sm hover:shadow-md transition-shadow">
                <Statistic
                  title={
                    <span className="text-gray-600 font-medium">
                      <TeamOutlined className="mr-2" />
                      Total Inscripciones
                    </span>
                  }
                  value={datosReporte.inscripciones?.resumenGeneral?.totalInscripciones || 0}
                  valueStyle={{ color: '#0047bb', fontSize: '28px', fontWeight: 'bold' }}
                />
                <Divider className="my-2" />
                <div className="flex justify-between items-center">
                  <Text type="secondary" className="text-xs">
                    Confirmadas: {datosReporte.inscripciones?.desglose?.confirmadas?.total || 0}
                  </Text>
                  <Text type="success" className="text-xs">
                    {datosReporte.inscripciones?.resumenGeneral?.tasaConfirmacion || 0}% confirmación
                  </Text>
                </div>
              </Card>
            </Col>

            <Col xs={24} sm={12} lg={6}>
              <Card className="shadow-sm hover:shadow-md transition-shadow">
                <Statistic
                  title={
                    <span className="text-gray-600 font-medium">
                      <PercentageOutlined className="mr-2" />
                      Ocupación Promedio
                    </span>
                  }
                  value={datosReporte.ocupacion?.talleres?.resumen?.promedioOcupacion || 0}
                  precision={2}
                  suffix="%"
                  valueStyle={{ color: '#faad14', fontSize: '28px', fontWeight: 'bold' }}
                />
                <Divider className="my-2" />
                <Progress 
                  percent={parseFloat(datosReporte.ocupacion?.talleres?.resumen?.promedioOcupacion || 0)} 
                  strokeColor="#0047bb"
                  showInfo={false}
                  size="small"
                />
              </Card>
            </Col>

            <Col xs={24} sm={12} lg={6}>
              <Card className="shadow-sm hover:shadow-md transition-shadow">
                <Statistic
                  title={
                    <span className="text-gray-600 font-medium">
                      <CalendarOutlined className="mr-2" />
                      Cupos Ocupados
                    </span>
                  }
                  value={datosReporte.ocupacion?.talleres?.resumen?.totalCuposOcupados || 0}
                  suffix={`/ ${datosReporte.ocupacion?.talleres?.resumen?.totalCuposDisponibles || 0}`}
                  valueStyle={{ color: '#722ed1', fontSize: '28px', fontWeight: 'bold' }}
                />
                <Divider className="my-2" />
                <Text type="secondary" className="text-xs">
                  {datosReporte.ocupacion?.talleres?.resumen?.totalTalleres || 0} talleres activos
                </Text>
              </Card>
            </Col>
          </Row>

          {/* Estadísticas Secundarias */}
          <Row gutter={[16, 16]} className="mb-6">
            {/* Estado de Pagos */}
            <Col xs={24} lg={12}>
              <Card 
                title={
                  <span className="text-lg font-semibold">
                    <CheckCircleOutlined className="mr-2 text-blue-500" />
                    Estado de Pagos
                  </span>
                }
                className="shadow-sm"
                extra={
                  <Button 
                    type="link" 
                    onClick={() => navigate('/reporteria/financiero/pagos')}
                    style={{ color: '#0047bb' }}
                  >
                    Ver detalle →
                  </Button>
                }
              >
                <Row gutter={[16, 16]}>
                  <Col span={8}>
                    <Statistic
                      title="Aprobados"
                      value={datosReporte.pagos?.desglose?.aprobados?.cantidad || 0}
                      valueStyle={{ color: '#0047bb', fontSize: '24px' }}
                      prefix={<CheckCircleOutlined />}
                      suffix={
                        <Text type="secondary" className="text-xs block">
                          {formatearMoneda(datosReporte.pagos?.desglose?.aprobados?.monto || 0)}
                        </Text>
                      }
                    />
                  </Col>
                  <Col span={8}>
                    <Statistic
                      title="Pendientes"
                      value={datosReporte.pagos?.desglose?.pendientes?.cantidad || 0}
                      valueStyle={{ color: '#faad14', fontSize: '24px' }}
                      prefix={<ClockCircleOutlined />}
                      suffix={
                        <Text type="secondary" className="text-xs block">
                          {formatearMoneda(datosReporte.pagos?.desglose?.pendientes?.monto || 0)}
                        </Text>
                      }
                    />
                  </Col>
                  <Col span={8}>
                    <Statistic
                      title="Rechazados"
                      value={datosReporte.pagos?.desglose?.rechazados?.cantidad || 0}
                      valueStyle={{ color: '#ff4d4f', fontSize: '24px' }}
                      prefix={<CloseCircleOutlined />}
                      suffix={
                        <Text type="secondary" className="text-xs block">
                          {formatearMoneda(datosReporte.pagos?.desglose?.rechazados?.monto || 0)}
                        </Text>
                      }
                    />
                  </Col>
                </Row>
                
                <Divider />
                
                <div className="flex justify-between">
                  <div>
                    <Text type="secondary">Tasa de Éxito:</Text>
                    <br />
                    <Text strong style={{ color: '#0047bb', fontSize: '20px' }}>
                      {datosReporte.pagos?.resumenGeneral?.tasaExito || 0}%
                    </Text>
                  </div>
                  <div className="text-right">
                    <Text type="secondary">Monto Total:</Text>
                    <br />
                    <Text strong style={{ fontSize: '20px' }}>
                      {formatearMoneda(datosReporte.pagos?.resumenGeneral?.montoTotal || 0)}
                    </Text>
                  </div>
                </div>
              </Card>
            </Col>

            {/* Top Talleres por Ingresos */}
            <Col xs={24} lg={12}>
              <Card 
                title={
                  <span className="text-lg font-semibold">
                    <TrophyOutlined className="mr-2 text-yellow-500" />
                    Top Talleres por Ingresos
                  </span>
                }
                className="shadow-sm"
                extra={
                  <Button 
                    type="link" 
                    onClick={() => navigate('/reporteria/financiero/ingresos')}
                    style={{ color: '#0047bb' }}
                  >
                    Ver detalle →
                  </Button>
                }
              >
                <div className="space-y-3">
                  {datosReporte.ingresos?.topTalleres && datosReporte.ingresos.topTalleres.length > 0 ? (
                    datosReporte.ingresos.topTalleres.slice(0, 5).map((taller, index) => (
                      <div key={index} className="flex justify-between items-center p-3 bg-gray-50 rounded hover:bg-gray-100 transition-colors">
                        <div className="flex items-center space-x-3">
                          <Badge 
                            count={index + 1} 
                            style={{ 
                              backgroundColor: index === 0 ? '#faad14' : index === 1 ? '#d9d9d9' : index === 2 ? '#cd7f32' : '#8c8c8c',
                              minWidth: '28px',
                              height: '28px',
                              lineHeight: '28px',
                              fontSize: '14px'
                            }}
                          />
                          <div>
                            <Text strong className="block">{taller.tallerNombre}</Text>
                            <Text type="secondary" className="text-xs block">
                              {taller.recintoNombre} • {taller.totalInscripciones} inscripciones
                            </Text>
                          </div>
                        </div>
                        <div className="text-right">
                          <Text strong style={{ color: '#0047bb', fontSize: '18px' }}>
                            {formatearMoneda(taller.totalIngresos)}
                          </Text>
                          <br />
                          <Text type="secondary" className="text-xs">
                            Prom: {formatearMoneda(taller.ingresoPromedioPorInscrito)}
                          </Text>
                        </div>
                      </div>
                    ))
                  ) : (
                    <Empty description="No hay datos de talleres" />
                  )}
                </div>
              </Card>
            </Col>
          </Row>

          {/* Distribución por Día y Franja Horaria */}
          <Row gutter={[16, 16]} className="mb-6">
            {/* Día más demandado */}
            <Col xs={24} md={12}>
              <Card 
                title={
                  <span className="text-lg font-semibold">
                    <CalendarOutlined className="mr-2 text-blue-500" />
                    Días Más Demandados
                  </span>
                }
                className="shadow-sm"
              >
                {datosReporte.ocupacion?.distribucion?.porDiaSemana?.distribucion ? (
                  <div className="space-y-2">
                    {datosReporte.ocupacion.distribucion.porDiaSemana.distribucion
                      .slice(0, 5)
                      .map((dia, index) => (
                        <div key={index} className="flex justify-between items-center">
                          <Text strong className="capitalize">{dia.dia}</Text>
                          <div className="flex-1 mx-4">
                            <Progress 
                              percent={parseFloat(dia.porcentaje)} 
                              strokeColor={index === 0 ? '#0047bb' : '#0047bb'}
                              size="small"
                            />
                          </div>
                          <Text>{dia.cantidad} inscripciones</Text>
                        </div>
                      ))}
                  </div>
                ) : (
                  <Empty description="No hay datos" />
                )}
              </Card>
            </Col>

            {/* Franja horaria más demandada */}
            <Col xs={24} md={12}>
              <Card 
                title={
                  <span className="text-lg font-semibold">
                    <ClockCircleOutlined className="mr-2 text-purple-500" />
                    Franjas Horarias
                  </span>
                }
                className="shadow-sm"
              >
                {datosReporte.ocupacion?.distribucion?.porFranjaHoraria?.distribucion ? (
                  <div className="space-y-3">
                    {datosReporte.ocupacion.distribucion.porFranjaHoraria.distribucion.map((franja, index) => (
                      <div key={index} className="p-3 bg-gray-50 rounded">
                        <div className="flex justify-between items-center mb-2">
                          <Text strong className="capitalize">{franja.franja}</Text>
                          <Badge count={`${franja.porcentaje}%`} style={{ backgroundColor: '#722ed1' }} />
                        </div>
                        <div className="flex justify-between items-center">
                          <Text type="secondary" className="text-xs">{franja.rango}</Text>
                          <Text>{franja.cantidad} inscripciones</Text>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <Empty description="No hay datos" />
                )}
              </Card>
            </Col>
          </Row>

          {/* Accesos Rápidos a Reportes */}
          <Card 
            title={
              <span className="text-lg font-semibold">
                <ShopOutlined className="mr-2" />
                Accesos Rápidos a Reportes
              </span>
            }
            className="shadow-sm"
          >
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} md={8}>
                <Card 
                  hoverable
                  className="text-center cursor-pointer"
                  onClick={() => navigate('/reporteria/financiero/ingresos')}
                >
                  <DollarOutlined style={{ fontSize: '32px', color: '#0047bb' }} />
                  <Title level={5} className="mt-3 mb-1">Ingresos y Ventas</Title>
                  <Text type="secondary" className="text-xs">
                    Análisis detallado de ingresos por periodo
                  </Text>
                </Card>
              </Col>

              <Col xs={24} sm={12} md={8}>
                <Card 
                  hoverable
                  className="text-center cursor-pointer"
                  onClick={() => navigate('/reporteria/operativo/ocupacion')}
                >
                  <CalendarOutlined style={{ fontSize: '32px', color: '#0047bb' }} />
                  <Title level={5} className="mt-3 mb-1">Ocupación y Cupos</Title>
                  <Text type="secondary" className="text-xs">
                    Estado de ocupación de talleres y horarios
                  </Text>
                </Card>
              </Col>

              <Col xs={24} sm={12} md={8}>
                <Card 
                  hoverable
                  className="text-center cursor-pointer"
                  onClick={() => navigate('/reporteria/operativo/inscripciones')}
                >
                  <TeamOutlined style={{ fontSize: '32px', color: '#722ed1' }} />
                  <Title level={5} className="mt-3 mb-1">Inscripciones</Title>
                  <Text type="secondary" className="text-xs">
                    Análisis completo de inscripciones
                  </Text>
                </Card>
              </Col>

              <Col xs={24} sm={12} md={8}>
                <Card 
                  hoverable
                  className="text-center cursor-pointer"
                  onClick={() => navigate('/reporteria/usuarios/demografia')}
                >
                  <UserOutlined style={{ fontSize: '32px', color: '#eb2f96' }} />
                  <Title level={5} className="mt-3 mb-1">Demografía</Title>
                  <Text type="secondary" className="text-xs">
                    Perfil demográfico de usuarios
                  </Text>
                </Card>
              </Col>

              <Col xs={24} sm={12} md={8}>
                <Card 
                  hoverable
                  className="text-center cursor-pointer"
                  onClick={() => navigate('/reporteria/usuarios/comportamiento')}
                >
                  <RiseOutlined style={{ fontSize: '32px', color: '#13c2c2' }} />
                  <Title level={5} className="mt-3 mb-1">Comportamiento</Title>
                  <Text type="secondary" className="text-xs">
                    Análisis de retención y patrones de uso
                  </Text>
                </Card>
              </Col>

              <Col xs={24} sm={12} md={8}>
                <Card 
                  hoverable
                  className="text-center cursor-pointer"
                  onClick={() => navigate('/reporteria/operativo/talleres')}
                >
                  <LineChartOutlined style={{ fontSize: '32px', color: '#faad14' }} />
                  <Title level={5} className="mt-3 mb-1">Talleres y Horarios</Title>
                  <Text type="secondary" className="text-xs">
                    Desempeño y análisis de talleres
                  </Text>
                </Card>
              </Col>
            </Row>
          </Card>
        </>
      )}
    </div>
  );
};

export default DashboardReporteria;
